import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey, TicketKey } from 'flow/types/EntityKeys';
import { selectIsSinglePaneMode } from 'flow/selectors/app-selectors';
import {
    selectCallRecordConnectedTime,
    selectCallRecordPhoneNumber,
    selectCallRecordQueueKey,
} from 'flow/selectors/call-records-selectors';
import { isAppCti } from 'flow/utils/app-utils';
import BackButtonCallRecordPage from 'flow/components/call-history-v2/BackButtonCallRecordPage';
import CallRecordCounterpartIdentity from 'flow/components/call-history-v2/CallRecordCounterpartIdentity';
import CtiHistoryEntitiesButton from 'flow/components/integration/CtiHistoryEntitiesButton';
import ButtonCreateShowTicket from '../misc/ButtonCreateShowTicket/ButtonCreateShowTicket';
import { SegmentContextType } from 'flow/components/call-history-v2/types';
import CallRecordCallButton from './CallRecordCallButton';
import CallRecordContactButton from './CallRecordContactButton';

interface Props {
    callRecordKey: CallRecordKey;
    pageTypeDemandsBackButton?: boolean;
    className?: string;
    subject?: string;
    ticketKey?: TicketKey;
    ticketScopeIsResolved: boolean;
    segmentContext: SegmentContextType;
}

const HeaderCallRecordDetail = ({
    callRecordKey,
    pageTypeDemandsBackButton,
    className,
    subject,
    ticketKey,
    ticketScopeIsResolved,
    segmentContext,
}: Props) => {
    const isSinglePaneMode = useAppSelector(selectIsSinglePaneMode);
    const showCtiWidget = useAppSelector(isAppCti);
    const queueKey = useAppSelector((state) =>
        selectCallRecordQueueKey(state, callRecordKey),
    );
    const callerNumber = useAppSelector((state) =>
        selectCallRecordPhoneNumber(state, callRecordKey),
    );
    const connected = useAppSelector((state) =>
        selectCallRecordConnectedTime(state, callRecordKey),
    );

    if (!connected) return null;

    const backButtonIsVisible = pageTypeDemandsBackButton || isSinglePaneMode;

    return (
        <header
            className={twMerge('flex h-16 w-full items-center px-4', className)}
        >
            <div className="flex min-w-0 flex-1 items-center gap-2">
                {backButtonIsVisible && <BackButtonCallRecordPage />}
                <CallRecordCounterpartIdentity
                    callRecordKey={callRecordKey}
                    subject={subject}
                />
            </div>
            <div className="flex items-center gap-2">
                {showCtiWidget && (
                    <CtiHistoryEntitiesButton
                        callRecordKey={callRecordKey}
                        numberE164={callerNumber}
                    />
                )}
                {(ticketKey || queueKey) && (
                    <ButtonCreateShowTicket
                        queueKey={queueKey}
                        callRecordKey={callRecordKey}
                        ticketKey={ticketKey}
                        ticketScopeIsResolved={ticketScopeIsResolved}
                        callerNumber={callerNumber}
                        callTimestamp={connected}
                        segmentContext={segmentContext}
                    />
                )}
                <CallRecordContactButton callRecordKey={callRecordKey} />
                <CallRecordCallButton callRecordKey={callRecordKey} />
            </div>
        </header>
    );
};

export default HeaderCallRecordDetail;
